import React from 'react';
import PropTypes from 'prop-types';
import { Form, FormSpy } from '../FormElements';
import Button from '../Button';
import StepIndicator from '../StepIndicator';
import { PTStep } from '../StepIndicator/PTStepIndicator';
import { PAYMENT_MODE, PAYMENT_MODE_TEST } from '../../../utils/payments';
import DonationFormAlert from '../DonationFormAlert';
import DonationFormExternalDonateButton from '../../03_organisms/DonationFormGenerics/DonationFormExternalDonateButton';
import ImpactText from '../ImpactText';
import {
  getFundraisingCosts,
  getFundraisingCostsExplanation,
  convertPaymentMethodToFormula,
  getImpact,
  isFundraisingCostsABTestVariantB,
} from '../../../utils/donationForm';
import DonationReview from '../../03_organisms/DonationForm/steps/06_PaymentsStep/DonationReview';
import ReCaptchaGoogle from '../ReCaptchaGoogle';
import FundraisingCosts from '../../02_molecules/FundraisingCosts';
import { SD } from '../../../utils/constants';
import FundraisingCostsVariantB from '../../02_molecules/FundraisingCosts/FundraisingCostsVariantB';

const debug = require('debug')('cw:FormWizard');

// TODO: lift up state to DonationFormPage because it needs to know more
// about steps management. Currently, we pass required info in callbacks.
// Maybe move away from this wizard completely.
class FormWizard extends React.Component {
  static Step = ({ children }) => children;

  constructor(props) {
    super(props);

    const initialValues = props.initialValues ?? {};

    const formula = convertPaymentMethodToFormula(
      initialValues.payment_method ? initialValues.payment_method : '',
    );

    this.state = {
      step: 0,
      values: {
        ...initialValues,
        captcha_executing: false,
        captcha_token: '',
        previousAmount: initialValues.amount ? initialValues.amount : 0,
        fundraisingCostsAmount: initialValues.amount
          ? getFundraisingCosts(initialValues.amount)
          : 0,
        fundraisingCostsFormula: getFundraisingCostsExplanation(formula),
        fundraisingCostsApplied:
          initialValues.fundraising_costs_init_value &&
          initialValues.type === SD &&
          initialValues.payment_method &&
          (initialValues.payment_method.startsWith('decoupled_stripe_credit_card_') ||
            initialValues.payment_method.startsWith('decoupled_stripe_button_') ||
            initialValues.payment_method.startsWith('decoupled_stripe_revolut_')),
        totalAmount: initialValues.amount
          ? Number(initialValues.amount) + Number(getFundraisingCosts(initialValues.amount))
          : 0,
        previousPaymentMethod: initialValues.payment_method ? initialValues.payment_method : '',
      },
      stepIndicatorValues: props.initialStepsForStepIndicator,
    };

    const queryParams = props.queryParams ?? {};

    this.forceCaptcha = parseInt(queryParams.forceCaptcha, 10) === 1;

    this.nextStep = this.nextStep.bind(this);
    this.goToStep = this.goToStep.bind(this);
    this.handleValidation = this.handleValidation.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCaptchaChallenge = this.handleCaptchaChallenge.bind(this);
    this.handleCaptchaError = this.handleCaptchaError.bind(this);
    this.reCaptchaRef = React.createRef();
    this.disableStepsInStepIndicator = this.disableStepsInStepIndicator.bind(this);
    this.onChangeForm = this.onChangeForm.bind(this);
    this.addPropsToStep = this.addPropsToStep.bind(this);
    this.getImpactComponent = this.getImpactComponent.bind(this);
  }

  /**
   * NOTE: Both handleValidation and handleSubmit switching are implemented
   * here because Redux Final Form does not accept changes to those
   * functions once the form has been defined.
   */
  handleValidation(values) {
    const { children } = this.props;
    const { step } = this.state;
    const activeStep = React.Children.toArray(children)[step];

    return activeStep.props.validate ? activeStep.props.validate(values) : {};
  }

  /**
   * Handles the whole donation form's submission.
   */
  async handleSubmit(values, { mutators }) {
    const { children, onSubmit, showCaptchaChallenge = () => false } = this.props;
    const { step } = this.state;
    const isLastStep = step === React.Children.count(children) - 1;

    // Forbid execution if captcha is executing and token isn't received yet. It helps to avoid
    // sending donation requests without captcha tokens that happens if the speed of connection
    // is slow and a user has ability to hit "Continue" several times before the captcha popup
    // loads and appears.
    if (values.captcha_executing && !values.captcha_token.length) {
      return;
    }

    if (isLastStep) {
      if (
        showCaptchaChallenge(values.payment_method, 'credit_card') &&
        !values.captcha_executing &&
        // Checking if either we are forcing captcha for testing purposes or we are in production
        (this.forceCaptcha || PAYMENT_MODE !== PAYMENT_MODE_TEST)
      ) {
        // Set the flag that captcha has started execution to display
        // the loading popup and disable form submission.
        mutators.setValue('captcha_executing', true);
        // Trigger actual captcha execution.
        return this.reCaptchaRef.current.execute();
      }

      return onSubmit(values);
    }
    return this.nextStep(mutators.setValue);
  }

  /**
   * Finishing callback for execution of reCaptcha challenge.
   */
  handleCaptchaChallenge(token, setFormValue, handleSubmit) {
    try {
      // Set token's value as a form field value.
      setFormValue('captcha_token', token);

      // Submit the whole donation form.
      handleSubmit();

      // As soon as the form is submitted we want to reset the captcha
      // challenge, so that the next form submit (i.e. in case of error)
      // will execute a new challenge with the new token. Otherwise
      // Google will complain on duplicated tokens sent multiple times.
      this.reCaptchaRef.current.reset();
      setFormValue('captcha_executing', false);
    } catch (e) {
      debug('Could not handle submission with captcha: %O', e);
    }
  }

  /**
   * Handles captcha error (or timeout).
   */
  handleCaptchaError(setFormValue) {
    // Silently reload the captcha and set the captcha challenge to false,
    // so that the next form submission will trigger the new captcha challenge.
    this.reCaptchaRef.current.reset();
    setFormValue('captcha_executing', false);
  }

  onChangeForm(formState) {
    const newFormState = { ...formState };
    const { onChange = () => {}, initialValues = {} } = this.props;

    // If step is not valid we have to be sure that donor can't
    // jump to the next steps by clicking on the next step.
    if (!formState.valid && formState.values.step) {
      this.disableStepsInStepIndicator(formState.values.step.index);
    }

    // In case of UK address Manually form we need to be sure that
    // addressLine1 and postalCode values were converted to string format.
    // We need it to be able to pre-populate addressLine1 and postalCode
    // text fields with right values. We have broken values in redux because
    // user selected addressLine1 and postalCode with Select widget.
    if (
      formState.values.addressSchemaType &&
      formState.values.addressSchemaType.type === 'manually'
    ) {
      if (typeof formState.values.address.addressLine1 === 'object') {
        newFormState.values.address.addressLine1 = formState.values.address.addressLine1.label;
      }
      if (typeof formState.values.address.postalCode === 'object') {
        newFormState.values.address.postalCode = formState.values.address.postalCode.label;
      }
    }

    // Reset Cardholder Name value when payment method isn't Credit Card.
    if (
      !formState.values.payment_method.startsWith('decoupled_stripe_credit_card_') &&
      !!formState.values?.payment_options?.name
    ) {
      // The approach taken from https://stackoverflow.com/a/64074947
      // to avoid "Cannot assign to read only property 'name' of object '[object Object]'"
      newFormState.values.payment_options = { ...newFormState.values.payment_options, name: '' };
    }

    // Conditions to check if we need to recalculate.
    const isAmountChanged = formState.values.amount !== formState.values.previousAmount;
    const isPaymentMethodChanged =
      formState.values.payment_method !== formState.values.previousPaymentMethod;

    // Recalculate and update fundraising costs and total amount
    // on payment method or amount change.
    if (isFundraisingCostsABTestVariantB()) {
      // Re-calculate Fundraising costs and apply it to the new amount.
      if (isAmountChanged) {
        const formula = convertPaymentMethodToFormula(formState.values.payment_method);
        newFormState.values.previousAmount = formState.values.amount;
        const fundraisingCostsAmount = getFundraisingCosts(formState.values.amount, formula);
        // Update fundraising costs and total amount.
        newFormState.values.fundraisingCostsAmount = fundraisingCostsAmount;
        newFormState.values.fundraisingCostsFormula = getFundraisingCostsExplanation(formula);
        newFormState.values.totalAmount =
          Number(formState.values.amount) + Number(fundraisingCostsAmount);
      }
    } else {
      if (isAmountChanged || isPaymentMethodChanged) {
        // Reset fundraising cost to its default state on payment method change.
        if (isPaymentMethodChanged) {
          newFormState.values.previousPaymentMethod = formState.values.payment_method;
          if (initialValues.fundraising_costs_init_value && formState.values.type === SD) {
            newFormState.values.fundraisingCostsApplied = true;
            newFormState.values.fundraising_costs = ['fundraising_costs'];
          } else {
            newFormState.values.fundraisingCostsApplied = false;
            newFormState.values.fundraising_costs = [];
          }
        }

        // Update previous amount on amount change.
        if (isAmountChanged) {
          newFormState.values.previousAmount = formState.values.amount;
        }

        // Recalculate costs based on payment method formula.
        // let formula = 'stripe';
        // if (formState.values.payment_method.startsWith('paypal_')) {
        //   formula = 'paypal';
        // } else if (formState.values.payment_method.startsWith('decoupled_stripe_revolut_')) {
        //   formula = 'revolut';
        // }
        const formula = convertPaymentMethodToFormula(formState.values.payment_method);
        const fundraisingCostsAmount = getFundraisingCosts(formState.values.amount, formula);
        // Update fundraising costs and total amount.
        newFormState.values.fundraisingCostsAmount = fundraisingCostsAmount;
        newFormState.values.fundraisingCostsFormula = getFundraisingCostsExplanation(formula);
        newFormState.values.totalAmount =
          Number(formState.values.amount) + Number(fundraisingCostsAmount);
      }

      // Updating state on fundraising costs checkbox change.
      const formHasFundraisingCostsCheckboxAndItIsTicked =
        formState.values.fundraising_costs &&
        formState.values.fundraising_costs[0] === 'fundraising_costs';
      const needUpdateFundraisingCostsApplied =
        (formHasFundraisingCostsCheckboxAndItIsTicked &&
          !formState.values.fundraisingCostsApplied) ||
        (!formHasFundraisingCostsCheckboxAndItIsTicked && formState.values.fundraisingCostsApplied);

      if (needUpdateFundraisingCostsApplied) {
        newFormState.values.fundraisingCostsApplied = !formState.values.fundraisingCostsApplied;
      }
    }

    onChange(newFormState);
  }

  getStepName(step) {
    const { children } = this.props;
    return React.Children.toArray(children)[step].props.name;
  }

  nextStep(setValueMutator) {
    const { children } = this.props;
    const { stepIndicatorValues, step } = this.state;

    this.setState(
      {
        step: Math.min(step + 1, children.length - 1),
        // We need to mark step as completed and clickable in StepIndicator.
        stepIndicatorValues: stepIndicatorValues.map((item) => {
          const newItem = item;
          if (item.index === step) {
            newItem.completed = true;
            newItem.clickable = true;
          }

          // Also we have to make next step clickable too,
          // to be able to jump between previous steps.
          if (item.index === step + 1) {
            newItem.clickable = true;
          }

          return newItem;
        }),
      },
      () => {
        // Mutator updates step form field when setState is completed.
        setValueMutator('step', { name: this.getStepName(step + 1), index: step + 1 });
      },
    );
  }

  goToStep(chosenStep, setValueMutator) {
    this.setState(
      {
        step: chosenStep.index,
      },
      () => {
        const { step } = this.state;
        // Mutator updates step form field when setState is completed.
        setValueMutator('step', { name: this.getStepName(step), index: step });
      },
    );
  }

  /**
   * This function disable clicking on the next steps in Step indicator.
   */
  disableStepsInStepIndicator(step) {
    const { stepIndicatorValues } = this.state;

    const newStepIndicatorValues = [];
    let needUpdate = false;
    stepIndicatorValues.forEach((item) => {
      const newItem = item;
      if (item.index > step && item.clickable) {
        newItem.clickable = false;
        needUpdate = true;
      }
      newStepIndicatorValues.push(newItem);
    });

    // Change state only if value was changed.
    if (needUpdate) {
      this.setState({ stepIndicatorValues: newStepIndicatorValues });
    }
  }

  addPropsToStep(activeStep, values, mutators) {
    let additionalProps = {};

    switch (activeStep.props.name) {
      case 'preferences':
        additionalProps = {
          setValue: mutators.setValue,
        };
        break;
      case 'giftaid':
        // Add onAccept and onCancel functions to giftAid step component.
        additionalProps = {
          onAccept: () => {
            mutators.setValue('giftaid', true);
            this.nextStep(mutators.setValue);
          },
          onCancel: () => {
            mutators.setValue('giftaid', false);
            this.nextStep(mutators.setValue);
          },
        };
        break;
    }

    additionalProps.setFormValue = mutators.setValue;

    // Add form values to step component.
    return React.cloneElement(activeStep, {
      children: React.cloneElement(activeStep.props.children, {
        values,
        ...additionalProps,
      }),
    });
  }

  getImpactComponent(donationType, amount) {
    const { impactsSingleDonation = [], impactsMonthlyDonation = [] } = this.props;
    const currentImpact = getImpact(
      +amount,
      donationType === SD ? impactsSingleDonation : impactsMonthlyDonation,
    );
    return currentImpact ? (
      <ImpactText text={currentImpact.text} className="donation-form-impact" />
    ) : null;
  }

  render() {
    const {
      children,
      submitButtonLabel = 'Submit',
      hideSubmitButton = false,
      showCaptchaChallenge = () => false,
      formTitle = '',
      onButtonClick = () => {},
      isLocked,
      showFundraisingCosts = false,
      fundraisingCostsTooltip = '',
    } = this.props;
    const { step, values: initialValues, stepIndicatorValues } = this.state;
    const activeStep = React.Children.toArray(children)[step];
    const isLastStep = step === React.Children.count(children) - 1;
    const isGiftAidStep = activeStep.props.name === 'giftaid';

    // Prepare currentStep for stepIndicator.
    // We use >= condition to cover cases when current step doesn't have
    // stepIndicatorName (Cases when steps display in between steps in step indicator).
    let currentStepForStepIndicator = stepIndicatorValues[0];
    stepIndicatorValues.forEach((item) => {
      if (step >= item.index) {
        currentStepForStepIndicator = item;
      }
    });

    // Prepare "next" button label.
    const nextButtonLabel =
      React.Children.toArray(children)[step].props.children.props.nextButtonLabel || 'NEXT STEP';

    return (
      <Form
        initialValues={initialValues}
        mutators={{
          // Create a mutator which allows to modify field's value by external callbacks.
          setValue: ([fieldName, value], state, { changeValue }) => {
            changeValue(state, fieldName, () => value);
          },
        }}
        subscription={{
          submitting: true,
          valid: true,
          values: true,
          hasValidationErrors: true,
          errors: true,
        }}
        validate={this.handleValidation}
        onSubmit={this.handleSubmit}
      >
        {/* eslint-disable-next-line no-return-assign */}
        {({
          handleSubmit,
          submitting,
          values,
          valid,
          form: { mutators },
          hasValidationErrors,
          errors,
        }) => (
          <form
            id="donation-form"
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              // Prevent form submit by Enter when IE search address functionality is focused.
              if (event.key === 'Enter' && event.shiftKey === false) {
                const ieAddressSearchElement = document.getElementById('autocomplete-search-ie');
                const focusedElement = document.activeElement;
                if (
                  ieAddressSearchElement &&
                  focusedElement &&
                  ieAddressSearchElement.contains(focusedElement)
                ) {
                  event.preventDefault();
                }
              }
            }}
          >
            <FormSpy onChange={(state) => this.onChangeForm(state)} />

            <div className="donation-form-container form-card">
              <div className="donation-form-title">
                <h1 className="large">{formTitle}</h1>
              </div>

              <StepIndicator
                steps={stepIndicatorValues}
                currentStep={currentStepForStepIndicator}
                onChange={(chosenStep) => this.goToStep(chosenStep, mutators.setValue)}
              />

              <div className="donation-form-active-step-container">
                {this.getImpactComponent(values.type, values.amount)}
                <DonationFormAlert />

                {/**
                 * In some cases we need to add props to the component.
                 * Render component with all props.
                 */}
                {this.addPropsToStep(activeStep, values, mutators)}

                {!isLastStep && !isGiftAidStep && (
                  <Button
                    id="donation-next-step-button"
                    isBlock
                    size="extra-large"
                    className={`primary-button ${!valid ? 'button-disabled' : ''}`}
                    onClick={() => hasValidationErrors && onButtonClick(errors)}
                  >
                    {nextButtonLabel}
                  </Button>
                )}

                {isLastStep &&
                  values.payment_method &&
                  showCaptchaChallenge(values.payment_method) &&
                  (this.forceCaptcha || PAYMENT_MODE !== PAYMENT_MODE_TEST) && (
                    <ReCaptchaGoogle
                      forwardedRef={this.reCaptchaRef}
                      onCaptchaChallenge={(token) =>
                        this.handleCaptchaChallenge(token, mutators.setValue, handleSubmit)
                      }
                      onCaptchaError={() => this.handleCaptchaError(mutators.setValue)}
                    />
                  )}

                {isLastStep &&
                  showFundraisingCosts &&
                  values.type === SD &&
                  (isFundraisingCostsABTestVariantB() ? (
                    <FundraisingCostsVariantB setValue={mutators.setValue} />
                  ) : (
                    <FundraisingCosts tooltipText={fundraisingCostsTooltip} />
                  ))}

                {isLastStep && !hideSubmitButton && (
                  <Button
                    // use className instead of isDisabled flag because
                    // tests check for validation error
                    className={`primary-button submit-donation ${
                      submitting || isLocked || values.captcha_executing || !valid
                        ? 'button-disabled'
                        : ''
                    }`}
                    isLoading={values.captcha_executing}
                    isBlock
                    size="extra-large"
                    id="donation-submit-button"
                    onClick={() => hasValidationErrors && onButtonClick(errors)}
                  >
                    {submitButtonLabel}
                  </Button>
                )}
                <DonationFormExternalDonateButton
                  currentStepName={values.step.name || 'amount'}
                  paymentMethod={values.payment_method}
                  donationType={values.type}
                  captchaObject={this.reCaptchaRef.current}
                  withCaptcha={
                    showCaptchaChallenge(values.payment_method) &&
                    (this.forceCaptcha || PAYMENT_MODE !== PAYMENT_MODE_TEST)
                  }
                />
                {isLastStep && <DonationReview extraFeatures={this.props.extraFeatures} />}
              </div>
            </div>
          </form>
        )}
      </Form>
    );
  }
}

FormWizard.propTypes = {
  extraFeatures: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialValues: PropTypes.shape(),
  queryParams: PropTypes.shape(),
  showCaptchaChallenge: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onButtonClick: PropTypes.func,
  submitButtonLabel: PropTypes.string,
  hideSubmitButton: PropTypes.bool,
  formTitle: PropTypes.string,
  initialStepsForStepIndicator: PropTypes.arrayOf(
    PropTypes.shape({
      ...PTStep,
      index: PropTypes.number,
    }),
  ).isRequired,
  isLocked: PropTypes.bool.isRequired,
  impactsSingleDonation: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      text: PropTypes.string,
    }),
  ),
  impactsMonthlyDonation: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      text: PropTypes.string,
    }),
  ),
  showFundraisingCosts: PropTypes.bool,
  fundraisingCostsTooltip: PropTypes.string,
};

FormWizard.Step.displayName = 'FormWizard.Step';
export default FormWizard;
